import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, Globe, CalendarDays, Radio } from "lucide-react";

const PRESETS = [
  { key: "today", label: "Oggi", days: 0 },
  { key: "7", label: "7 giorni", days: 7 },
  { key: "30", label: "30 giorni", days: 30 },
  { key: "90", label: "90 giorni", days: 90 },
  { key: "custom", label: "Personalizzato" },
];

const isoDay = (d) => d.toISOString().slice(0, 10);
export const fmtAt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
};
export const siteLabel = (u) => (u || "").replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/$/, "");
export const SiteLink = ({ url, testId = "stats-site" }) => url ? (
  <a href={/^https?:\/\//i.test(url) ? url : `https://${url}`} target="_blank" rel="noopener noreferrer" data-testid={testId}
    className="inline-flex items-center gap-1 text-xs text-brand underline decoration-dotted underline-offset-2 hover:text-brand/80">
    <Globe className="h-3 w-3" /> {siteLabel(url)}
  </a>
) : <span className="text-xs text-muted-foreground">—</span>;

// Filtro data (preset + intervallo personalizzato)
export function DateRangeFilter({ value, onChange, testId = "stats-range" }) {
  const setPreset = (p) => {
    if (p.key === "custom") { onChange({ ...value, preset: "custom" }); return; }
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - p.days);
    onChange({ preset: p.key, from: isoDay(from), to: isoDay(to) });
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testId}>
      <CalendarDays className="h-4 w-4 text-muted-foreground" />
      {PRESETS.map((p) => (
        <Button key={p.key} type="button" size="sm" variant={value.preset === p.key ? "default" : "outline"} onClick={() => setPreset(p)}
          className={`h-8 rounded-full text-xs ${value.preset === p.key ? "bg-primary text-primary-foreground" : ""}`} data-testid={`${testId}-${p.key}`}>
          {p.label}
        </Button>
      ))}
      {value.preset === "custom" && (
        <div className="flex items-center gap-1.5 text-xs" data-testid={`${testId}-custom`}>
          <Input type="date" value={value.from || ""} onChange={(e) => onChange({ ...value, from: e.target.value })} className="h-8 w-[150px] text-xs" data-testid={`${testId}-from`} />
          <span className="text-muted-foreground">→</span>
          <Input type="date" value={value.to || ""} onChange={(e) => onChange({ ...value, to: e.target.value })} className="h-8 w-[150px] text-xs" data-testid={`${testId}-to`} />
        </div>
      )}
    </div>
  );
}

export function useStats(endpoint, { refreshMs = 30000 } = {}) {
  const [range, setRange] = useState(() => { const to = new Date(); const from = new Date(); from.setDate(to.getDate() - 30); return { preset: "30", from: isoDay(from), to: isoDay(to) }; });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const reqId = useRef(0);
  const load = useCallback(async (silent = false) => {
    if (!range.from || !range.to || range.from > range.to) return;
    const id = ++reqId.current;
    if (!silent) setLoading(true);
    try {
      const { data: d } = await api.get(endpoint, { params: { from: range.from, to: range.to } });
      if (id !== reqId.current) return; // risposta superata da una richiesta più recente
      setData(d); setUpdatedAt(new Date());
    } catch { /* toast evitato: refresh periodico */ } finally { if (id === reqId.current) setLoading(false); }
  }, [endpoint, range.from, range.to]);
  useEffect(() => { const t = setTimeout(() => load(), range.preset === "custom" ? 350 : 0); return () => clearTimeout(t); }, [load, range.preset]);
  useEffect(() => { if (!refreshMs) return undefined; const id = setInterval(() => load(true), refreshMs); return () => clearInterval(id); }, [load, refreshMs]);
  return { range, setRange, data, loading, updatedAt, reload: () => load(false) };
}

export function StatsToolbar({ range, setRange, updatedAt, loading, onReload, testId = "stats" }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-3" data-testid={`${testId}-toolbar`}>
      <DateRangeFilter value={range} onChange={setRange} testId={`${testId}-range`} />
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1" data-testid={`${testId}-live`}><Radio className="h-3 w-3 text-emerald-500" /> In tempo reale · aggiornamento ogni 30 s</span>
        {updatedAt && <span data-testid={`${testId}-updated`}>· {updatedAt.toLocaleTimeString("it-IT")}</span>}
        <Button type="button" size="sm" variant="ghost" onClick={onReload} disabled={loading} className="h-7 px-2" data-testid={`${testId}-refresh`}>
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );
}

export function KpiRow({ items, testId = "stats-kpi" }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid={testId}>
      {items.map((k) => (
        <div key={k.label} className="rounded-xl border border-border bg-card px-4 py-3" data-testid={`${testId}-${k.key}`}>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{k.label}</p>
          <p className={`mt-1 text-2xl font-bold ${k.tone || "text-foreground"}`}>{k.value ?? 0}</p>
        </div>
      ))}
    </div>
  );
}

export function RankingList({ title, rows, labelKey = "label", renderExtra, emptyText = "Nessun dato nel periodo", testId = "stats-ranking" }) {
  const max = useMemo(() => Math.max(1, ...(rows || []).map((r) => r.count || 0)), [rows]);
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {!rows?.length ? <p className="mt-3 text-sm text-muted-foreground" data-testid={`${testId}-empty`}>{emptyText}</p> : (
        <ol className="mt-3 space-y-2">
          {rows.map((r, i) => (
            <li key={r.key || i} className="text-sm" data-testid={`${testId}-row`}>
              <div className="flex items-center justify-between gap-3">
                <span className="flex min-w-0 items-center gap-2">
                  <span className="w-5 shrink-0 text-xs font-bold text-muted-foreground">{i + 1}.</span>
                  <span className="truncate font-medium text-foreground">{r[labelKey]}</span>
                  {renderExtra && renderExtra(r)}
                </span>
                <Badge variant="secondary" className="shrink-0 rounded-full" data-testid={`${testId}-count`}>{r.count}</Badge>
              </div>
              <div className="ml-7 mt-1 h-1.5 overflow-hidden rounded-full bg-brema-bg"><div className="h-full rounded-full bg-primary/70" style={{ width: `${Math.round((r.count / max) * 100)}%` }} /></div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export function StatsTable({ title, columns, rows, emptyText = "Nessun evento nel periodo", testId = "stats-table", rowTestId }) {
  return (
    <div className="rounded-xl border border-border bg-card" data-testid={testId}>
      <div className="border-b border-border px-4 py-3"><h3 className="text-sm font-semibold text-foreground">{title} <span className="ml-1 text-xs font-normal text-muted-foreground">({rows?.length || 0})</span></h3></div>
      {!rows?.length ? <p className="px-4 py-6 text-sm text-muted-foreground" data-testid={`${testId}-empty`}>{emptyText}</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-brema-bg text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>{columns.map((c) => <th key={c.key} className="px-4 py-2.5 font-medium">{c.label}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r._id || r.key || i} className="border-t border-border/60 align-top" data-testid={rowTestId || `${testId}-row`}>
                  {columns.map((c) => <td key={c.key} className="px-4 py-2.5">{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export const InactiveBadge = ({ days, inactive }) => (
  <Badge className={`whitespace-nowrap rounded-full ${inactive ? "bg-amber-500/15 text-amber-700 dark:text-amber-400" : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"}`} variant="secondary">
    {inactive ? (days == null ? "Mai attivo" : `Inattivo · ${days} gg`) : `Attivo · ${days} gg fa`}
  </Badge>
);
